import { gql, useQuery } from "@apollo/client";
import Errors from "components/base/Errors";
import { DatePicker, Input, Select } from "components/base/Form";
import InputSelect from "components/base/InputSelect";
import Spinner from "components/base/Spinner";
import { useContext } from "react";
import { formatDate } from "react-day-picker/moment";
import ShipmentContext from "../ShipmentContext";

const transportationOptions = [
  "SEA",
  "SHIP",
  "AIR",
  "TRAIN",
  "COURIER",
  "SEA & AIR",
];

const FETCH_LOGISTICS_OPTIONS = gql`
  query FETCH_LOGISTICS_OPTIONS($zone: String!) {
    allPorts
    containers(zone: $zone) {
      name
      cbm
      cost
    }
  }
`;

const shippingTermContains = (shippingTerm, keyword) =>
  (shippingTerm || "").includes(keyword);

const LogisticsPlan = ({ shippingTerms, shippingZone }) => {
  const {
    state: {
      shippingTerm,
      requestCargoReadyDate,
      shipContainer,
      countryOfOrigin,
      etd,
      eta,
      newEtd,
      newEta,
      loadingPort,
      portOfDischarge,
      portOfDestination,
      shipBy,
      blNumber,
      containerNumber,
      vv,
      customer,
      shipFrom,
      shipTo,
    },
    dispatch,
    updateShipment,
  } = useContext(ShipmentContext);
  const { loading, error, data } = useQuery(FETCH_LOGISTICS_OPTIONS, {
    variables: { zone: shippingZone },
  });
  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  return (
    <div className="flex flex-col gap-6">
      <h2>Logistics</h2>
      <div className="flex flex-col gap-10 rounded-xl bg-white p-8 dark:bg-gray-900 dark:text-gray-300">
        <div className="flex flex-col gap-8 lg:flex-row lg:flex-wrap lg:items-start lg:gap-x-10">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
            <label className="lg:shrink-0">Country of Origin</label>
            <Input
              className="w-full lg:w-40"
              value={countryOfOrigin}
              onChange={(e) =>
                dispatch({
                  type: "changeShipment",
                  payload: { countryOfOrigin: e.target.value },
                })
              }
              onBlur={(e) =>
                updateShipment({ countryOfOrigin: e.target.value })
              }
            />
          </div>
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
            <label className="lg:shrink-0">Request Cargo Ready Date</label>
            <DatePicker
              className="w-full lg:w-40"
              format="YYYY-MM-DD"
              value={
                requestCargoReadyDate
                  ? new Date(requestCargoReadyDate * 1000)
                  : ""
              }
              onDayChange={(selectedDay) =>
                updateShipment({
                  requestCargoReadyDate: selectedDay
                    ? selectedDay.getTime() / 1000
                    : null,
                })
              }
            />
          </div>
          <div className="flex flex-col gap-2 lg:flex-row lg:flex-wrap lg:items-center lg:gap-3">
            <label className="lg:shrink-0">Shipping Term</label>
            <Select
              className="w-full lg:w-72"
              value={shippingTerm}
              onChange={(e) => updateShipment({ shippingTerm: e.target.value })}
            >
              <option value="">--- Select ---</option>
              {shippingTerms.map((term, index) => (
                <option key={index} value={term}>
                  {term}
                </option>
              ))}
            </Select>
            {(shippingTermContains(shippingTerm, "DELIVERED AT PLACE") ||
              shippingTermContains(shippingTerm, "DELIVERED DUTY PAID") ||
              shippingTermContains(shippingTerm, "DELIVERED AT TERMINAL")) && (
              <p className="w-fit max-w-full rounded-lg bg-red-50 px-3 py-2 text-sm text-red-500 dark:bg-red-950/30 dark:text-red-300">
                Please update the shipment status manually after the goods
                arrive!
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-8 lg:flex-row lg:flex-wrap lg:items-start lg:gap-x-10">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
              <label className="lg:shrink-0">Ship by</label>
              <Select
                className="w-full lg:w-40"
                value={shipBy}
                onChange={(e) => updateShipment({ shipBy: e.target.value })}
              >
                <option value="">--- Select ---</option>
                {transportationOptions.map((i, index) => (
                  <option key={index} value={i}>
                    {i}
                  </option>
                ))}
              </Select>
            </div>
            {(shipBy === "SEA" || shipBy === "TRAIN") && (
              <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
                <label className="lg:shrink-0">Container</label>
                <Select
                  className="w-full lg:w-40"
                  value={shipContainer}
                  onChange={(e) =>
                    updateShipment({ shipContainer: e.target.value })
                  }
                >
                  <option value="">--- Select a Container ---</option>
                  <option value="LCL">LCL</option>
                  {data.containers.map((container, index) => (
                    <option key={index} value={container.name}>
                      {container.name}
                    </option>
                  ))}
                </Select>
              </div>
            )}
          </div>

          {customer.system === "AB" ? (
            <div className="flex flex-col gap-8 lg:flex-row lg:flex-wrap lg:items-start lg:gap-x-10">
              <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
                <label className="lg:shrink-0">Port of Loading</label>
                <InputSelect
                  className="w-full lg:w-72"
                  inputClasses="w-full px-4"
                  value={loadingPort}
                  options={data.allPorts.map((i) => ({ name: i }))}
                  onChange={(loadingPort) =>
                    dispatch({
                      type: "changeShipment",
                      payload: { loadingPort },
                    })
                  }
                  onBlur={() => updateShipment({ loadingPort })}
                  onSelect={(option) =>
                    updateShipment({ loadingPort: option.name })
                  }
                />
              </div>
              <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
                <label className="lg:shrink-0">Port of Discharge</label>
                <InputSelect
                  className="w-full lg:w-72"
                  inputClasses="w-full px-4"
                  value={portOfDischarge}
                  options={data.allPorts.map((i) => ({ name: i }))}
                  onChange={(portOfDischarge) =>
                    dispatch({
                      type: "changeShipment",
                      payload: { portOfDischarge },
                    })
                  }
                  onBlur={() => updateShipment({ portOfDischarge })}
                  onSelect={(option) =>
                    updateShipment({ portOfDischarge: option.name })
                  }
                />
              </div>
              <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
                <label className="lg:shrink-0">Port of Destination</label>
                <InputSelect
                  className="w-full lg:w-72"
                  inputClasses="w-full px-4"
                  value={portOfDestination}
                  options={data.allPorts.map((i) => ({ name: i }))}
                  onChange={(portOfDestination) =>
                    dispatch({
                      type: "changeShipment",
                      payload: { portOfDestination },
                    })
                  }
                  onBlur={() => updateShipment({ portOfDestination })}
                  onSelect={(option) =>
                    updateShipment({ portOfDestination: option.name })
                  }
                />
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-8 lg:flex-row lg:flex-wrap lg:items-start lg:gap-x-10">
              <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
                <label className="lg:shrink-0">Ship From</label>
                <Input
                  className="w-full lg:w-40"
                  value={shipFrom}
                  onChange={(e) =>
                    dispatch({
                      type: "changeShipment",
                      payload: { shipFrom: e.target.value },
                    })
                  }
                  onBlur={(e) => updateShipment({ shipFrom: e.target.value })}
                />
              </div>
              <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
                <label className="lg:shrink-0">Ship To</label>
                <Input
                  className="w-full lg:w-40"
                  value={shipTo}
                  onChange={(e) => {
                    dispatch({
                      type: "changeShipment",
                      payload: { shipTo: e.target.value },
                    });
                  }}
                  onBlur={(e) => updateShipment({ shipTo: e.target.value })}
                />
              </div>
            </div>
          )}

          <div className="flex flex-col gap-8 lg:flex-row lg:flex-wrap lg:items-start lg:gap-x-10">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
              <label className="lg:shrink-0">ETD</label>
              {newEtd ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="line-through">
                    {formatDate(new Date(etd * 1000), "YYYY-MM-DD")}
                  </span>
                  <span>→</span>
                  <span>
                    {formatDate(new Date(newEtd * 1000), "YYYY-MM-DD")}
                  </span>
                  <span className="text-xs text-gray-600 dark:text-gray-300">
                    new ETD was set in errors, you can not change the original
                    ETA now.
                  </span>
                </div>
              ) : (
                <DatePicker
                  className="w-full lg:w-40"
                  format="YYYY-MM-DD"
                  value={etd ? new Date(etd * 1000) : ""}
                  disabled={newEtd}
                  onDayChange={(selectedDay) => {
                    const etd = selectedDay
                      ? selectedDay.getTime() / 1000
                      : null;
                    updateShipment({ etd });
                  }}
                />
              )}
            </div>
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
              <label className="lg:shrink-0">ETA</label>
              {newEta ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="line-through">
                    {formatDate(new Date(eta * 1000), "YYYY-MM-DD")}
                  </span>
                  <span>→</span>
                  <span>
                    {formatDate(new Date(newEta * 1000), "YYYY-MM-DD")}
                  </span>
                  <span className="text-xs text-gray-600 dark:text-gray-300">
                    new ETA was set in errors, you can not change the original
                    ETA now.
                  </span>
                </div>
              ) : (
                <DatePicker
                  className="w-full lg:w-40"
                  format="YYYY-MM-DD"
                  value={eta ? new Date(eta * 1000) : ""}
                  onDayChange={(selectedDay) => {
                    const eta = selectedDay
                      ? selectedDay.getTime() / 1000
                      : null;
                    updateShipment({ eta });
                  }}
                />
              )}
            </div>
          </div>

          <div className="flex flex-col gap-8 lg:flex-row lg:flex-wrap lg:items-start lg:gap-x-10">
            {customer.system === "AB" && (
              <>
                <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
                  <label className="lg:shrink-0">BL Number</label>
                  <Input
                    className="w-full lg:w-40"
                    type="text"
                    value={blNumber}
                    onChange={(e) =>
                      dispatch({
                        type: "changeShipment",
                        payload: { blNumber: e.target.value },
                      })
                    }
                    onBlur={(e) => updateShipment({ blNumber: e.target.value })}
                  />
                </div>
                <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
                  <label className="lg:shrink-0">Container Number</label>
                  <Input
                    className="w-full lg:w-40"
                    type="text"
                    value={containerNumber}
                    onChange={(e) =>
                      dispatch({
                        type: "changeShipment",
                        payload: { containerNumber: e.target.value },
                      })
                    }
                    onBlur={(e) =>
                      updateShipment({ containerNumber: e.target.value })
                    }
                  />
                </div>
              </>
            )}
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
              <label className="lg:shrink-0">
                {customer.system === "AB" ? "V/V" : "Tracking Number"}
              </label>
              <Input
                className={`w-full ${customer.system === "AB" ? "lg:w-72" : "lg:w-40"}`}
                type="text"
                value={vv}
                onChange={(e) =>
                  dispatch({
                    type: "changeShipment",
                    payload: { vv: e.target.value },
                  })
                }
                onBlur={(e) => updateShipment({ vv: e.target.value })}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogisticsPlan;
